import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { TableActionLink } from '../../components/ui/TableActionLink';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import {
  createPermissionGroup,
  deletePermissionGroup,
  listPermissionGroups,
  PermissionGroup,
} from '../../services/permissionGroupsApi';

export function PermissionGroupsPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('access-control.permission-groups');

  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: '', name: '', description: '' });

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listPermissionGroups({ page: targetPage, pageSize });
      setGroups(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch {
      toast.error('Falha ao carregar grupos de permissões.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    try {
      await createPermissionGroup({ ...form, isActive: true });
      toast.success('Grupo de permissões criado.');
      setForm({ code: '', name: '', description: '' });
      await loadData(page);
    } catch {
      toast.error('Falha ao criar grupo.');
    }
  }

  async function handleDelete(id: string): Promise<void> {
    if (!canWrite) return;
    if (
      !(await confirm({
        title: 'Excluir grupo',
        message: 'Excluir este grupo de permissões?',
        confirmLabel: 'Excluir',
        tone: 'danger',
      }))
    ) {
      return;
    }

    try {
      await deletePermissionGroup(id);
      toast.success('Grupo excluído.');
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir grupo. Verifique se não está em uso.');
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <PageShell>
      <PageHeader
        title="Grupos de permissões"
        description="Configure pacotes por módulo: Acesso (menu), Leitura (consulta), escrita e exclusão. Colaboradores recebem um perfil hierárquico e um ou mais grupos."
      />

      {canWrite ? (
        <form className="erp-form-card space-y-3" onSubmit={(event) => void handleSubmit(event)}>
          <h2 className="erp-form-section-title">Novo grupo</h2>
          <div className="erp-form-grid md:grid-cols-3">
            <label className="erp-label-block">
              Código
              <input
                className="erp-input"
                value={form.code}
                onChange={(event) => setForm((current) => ({ ...current, code: event.target.value }))}
                required
              />
            </label>
            <label className="erp-label-block">
              Nome
              <input
                className="erp-input"
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                required
              />
            </label>
            <label className="erp-label-block md:col-span-1">
              Descrição
              <input
                className="erp-input"
                value={form.description}
                onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
              />
            </label>
          </div>
          <button type="submit" className="erp-btn-primary">
            Criar grupo
          </button>
        </form>
      ) : null}

      {loading ? (
        <p className="erp-loading">Carregando...</p>
      ) : (
        <div className="erp-table-wrap">
          <table className="erp-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Nome</th>
                <th>Tipo</th>
                <th aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group.id}>
                  <td>{group.code}</td>
                  <td>{group.name}</td>
                  <td>{group.type === 'system' ? 'Sistema' : 'Customizado'}</td>
                  <td>
                    <div className="flex items-center gap-1">
                      <TableActionLink to={`/admin/permission-groups/${group.id}`}>Matriz</TableActionLink>
                      {canWrite && group.type !== 'system' ? (
                        <DeleteIconButton label={`Excluir ${group.name}`} onClick={() => void handleDelete(group.id)} />
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ListPaginationBar
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPrevious={() => setPage((current) => Math.max(1, current - 1))}
        onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
      />
    </PageShell>
  );
}
